<template>
  <div @keydown.ctrl.s.prevent="onPageSave">
    <top-bar :programId="programId"></top-bar>

    <v-dialog v-model="dialogDelete" persistent max-width="290">
      <v-card>
        <v-card-title class="text-h5"> Delete page {{ deletePageNumber }}? </v-card-title>
        <v-card-text>Are you sure you want to delete this page? This action cannot be undone.</v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="darken-1" text @click="dialogDelete = false"> Cancel </v-btn>
          <v-btn color="red darken-1" text @click="confirmRemovePage"> Delete </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-container class="pt-0" fluid>
      <v-row class="flex-nowrap">
        <v-col :cols="leftColumnSize">
          <v-card :height="height" class="d-flex flex-column" rounded="lg" outlined>
            <v-card-title class="document-builder grey lighten-5">
              <span class="text-truncate d-block text-h6 text-capitalize"> Document Layout Builder </span>
            </v-card-title>
            <v-divider></v-divider>
            <v-card-text class="pa-3 grey lighten-5">
              <v-btn block depressed class="text-capitalize" @click="editProgram" color="grey lighten-2">
                Edit Event & Team Info <v-icon small right>mdi-pencil-outline</v-icon>
              </v-btn>
            </v-card-text>
            <v-divider></v-divider>
            <v-card-text class="pa-3 grey lighten-5">
              <v-btn block depressed class="text-capitalize" color="grey lighten-2" @click="toggleNewPageModal">
                Create New Page <v-icon small right>mdi-file-plus-outline</v-icon>
              </v-btn>
            </v-card-text>
            <v-divider></v-divider>
            <v-card-text class="pa-0 grey lighten-5 overflow-y-auto flex-grow-1">
              <v-list color="grey lighten-3" dense class="py-0">
                <v-list-item
                  ref="pageItem0"
                  :ripple="false"
                  :input-value="currentPage === 0"
                  active-class="grey lighten-1"
                  class="px-0"
                  @click="selectPage(0)"
                  @keydown="moveNext($event, 'pageItem0')"
                >
                  <v-avatar size="40" tile color="grey lighten-4">1</v-avatar>
                  <v-list-item-content>
                    <v-list-item-title class="ml-2">Landing Page</v-list-item-title>
                  </v-list-item-content>
                </v-list-item>
                <v-divider></v-divider>
                <draggable :list="pages" ghost-class="ghost" @change="updateOrderPage">
                  <template>
                    <v-list-item
                      v-for="page in pages"
                      :ref="`pageItem${page.id}`"
                      :ripple="false"
                      active-class="grey lighten-1"
                      :input-value="currentPage === page.id"
                      class="px-0 item-list"
                      :key="page.id"
                      @click="selectPage(page.id)"
                    >
                      <v-avatar size="40" tile :color="`grey lighten-${currentPage === page.id ? '2' : '4'}`">
                        <input
                          class="page-number-input"
                          type="number"
                          :value="page.pageNumber"
                          :min="2"
                          :max="pages.length + 1"
                          @click.stop
                          @mousedown.stop
                          @touchstart.stop
                          @focus="selectPageNumberInput($event)"
                          @keydown.enter.stop="movePageToNumber(page, $event)"
                          @blur="movePageToNumber(page, $event)"
                        />
                      </v-avatar>
                      <v-list-item-content>
                        <v-list-item-title class="ml-2">{{ page.title }}</v-list-item-title>
                      </v-list-item-content>
                      <v-tooltip v-if="page.isHidden" bottom>
                        <template v-slot:activator="{ on, attrs }">
                          <v-icon class="pl-2" v-bind="attrs" v-on="on">mdi-eye-off</v-icon>
                        </template>
                        <span>Page is hidden</span>
                      </v-tooltip>
                      <v-menu class="elevation-2">
                        <template #activator="{ on, attrs }">
                          <v-btn
                            icon
                            v-bind="attrs"
                            v-on="on"
                            @click.stop
                            @mousedown.stop
                            @touchstart.stop
                            @keydown="moveNext($event, `pageItem${page.id}`)"
                          >
                            <v-icon>mdi-dots-vertical</v-icon>
                          </v-btn>
                        </template>
                        <v-list class="py-0">
                          <v-list-item @click="editPage(page.id)">
                            <v-list-item-icon class="mr-0">
                              <v-icon size="18">mdi-pencil-outline</v-icon>
                            </v-list-item-icon>
                            <v-list-item-content>
                              <v-list-item-title>Edit Page Details</v-list-item-title>
                            </v-list-item-content>
                          </v-list-item>
                          <v-list-item @click="removePage(page?.pageNumber, page.id)">
                            <v-list-item-icon class="mr-0">
                              <v-icon size="18">mdi-delete-outline</v-icon>
                            </v-list-item-icon>
                            <v-list-item-content>
                              <v-list-item-title>Remove Page</v-list-item-title>
                            </v-list-item-content>
                          </v-list-item>
                        </v-list>
                      </v-menu>
                    </v-list-item>
                  </template>
                </draggable>
              </v-list>
            </v-card-text>
            <v-divider></v-divider>
            <v-card-text class="pa-3 grey lighten-5">
              <v-btn
                block
                depressed
                class="text-capitalize mb-2"
                color="grey lighten-2"
                :disabled="!orderUpdated"
                @click="onSaveProgram"
              >
                Save
              </v-btn>
              <v-btn
                block
                depressed
                class="text-capitalize"
                color="grey lighten-2"
                :disabled="!canPublish"
                @click="onPublish"
              >
                Publish
              </v-btn>
            </v-card-text>
          </v-card>
        </v-col>
        <v-col :cols="centerColumnSize">
          <v-card :height="height" class="d-flex flex-column" outlined rounded="lg">
            <v-card-title class="grey lighten-5 py-3 justify-end" tabindex="0">
              <span class="text-h6 text-capitalize" ref="pageBuilder">Page Builder</span>
              <v-spacer></v-spacer>
              <div>
                <div v-if="currentPage" class="d-flex align-center gap-4">
                  <span class="grey--text text--darken-1 text-body-2">Page Template</span>
                  <v-text-field
                    v-if="page.templateId"
                    ref="pageBuilder"
                    class="text-body-1"
                    readonly
                    filled
                    dense
                    hide-details
                    :value="page.template.title"
                  ></v-text-field>
                  <v-text-field
                    v-else
                    ref="pageBuilder"
                    class="text-body-1"
                    readonly
                    filled
                    dense
                    hide-details
                    value="Imported Template"
                  ></v-text-field>
                  <hide-page-button />
                  <PageReimportButton />
                  <v-btn icon @click="editPage(page.id)">
                    <v-icon>mdi-pencil-outline</v-icon>
                  </v-btn>
                </div>
                <!-- <div class="field has-addons">
                  <v-card-text class="pa-3 grey lighten-5">
                    <v-text-field class="text-body-1" readonly filled dense hide-details disabled v-model="showURL">
                      <template slot="append">
                        <v-icon right @click.prevent="copyToClipboard('teste01')">mdi-content-copy</v-icon>
                        <v-icon right @click.prevent="copyToClipboard('teste02')">mdi-link</v-icon>
                      </template>
                    </v-text-field>
                  </v-card-text>
                </div> -->
              </div>
            </v-card-title>
            <v-divider></v-divider>
            <v-card-text class="grey lighten-5 flex-grow-1 overflow-y-auto">
              <div v-if="currentPage && displayLandingPage === false" class="d-flex flex-column align-stretch gap-4">
                <component
                  class="mb-"
                  v-for="block in page?.content?.blocks"
                  :is="blocksMap[block.type]"
                  :key="page.id + '-' + block.id"
                  v-model="block.data"
                  :config="{ ...block.config, isBlockBased: block?.isBlockBased }"
                />
              </div>
              <div v-else>
                <landing-page
                  v-if="programLoaded"
                  v-model="program.landingPage"
                  :clientId="program.clientId"
                  ref="form"
                  :isLoading="isLoading"
                  @loading-image="isLoadingImage = $event"
                ></landing-page>
              </div>
            </v-card-text>
            <v-divider></v-divider>
            <template v-if="!$vuetify.breakpoint.xlOnly">
              <v-card-actions class="pa-3 grey lighten-5" v-if="currentPage == 0">
                <v-btn
                  depressed
                  class="text-capitalize"
                  color="primary"
                  @click="onPageSave"
                  :disabled="isLoading || isLoadingImage"
                  :loading="isLoading"
                  @keydown="moveToSidebar"
                  >Save
                </v-btn>
                <v-spacer></v-spacer>
              </v-card-actions>
              <v-card-actions class="pa-3 grey lighten-5" v-else>
                <v-btn depressed class="text-capitalize" color="primary" @click="onPageSave">Save</v-btn>
                <v-spacer></v-spacer>
                <v-btn
                  @click="removePage(page?.pageNumber, page.id)"
                  @keydown="moveToSidebar"
                  icon
                  tile
                  depressed
                  light
                  class="grey lighten-2 text--primary rounded"
                >
                  <v-icon>mdi-delete-outline</v-icon>
                </v-btn>
              </v-card-actions>
            </template>
            <template v-else>
              <v-card-actions class="pa-3 grey lighten-5" v-if="currentPage == 0">
                <v-btn
                  depressed
                  class="text-capitalize"
                  color="primary"
                  @click="onPageSave"
                  :disabled="isLoading || isLoadingImage"
                  :loading="isLoading"
                  >Save
                </v-btn>
                <v-spacer></v-spacer>
              </v-card-actions>
              <v-card-actions class="pa-3 grey lighten-5" v-else>
                <v-btn depressed class="text-capitalize" color="primary" @click="onPageSave">Save</v-btn>
                <v-spacer></v-spacer>
                <v-btn
                  @click="removePage(page?.pageNumber, page.id)"
                  icon
                  tile
                  depressed
                  light
                  class="grey lighten-2 text--primary rounded"
                >
                  <v-icon>mdi-delete-outline</v-icon>
                </v-btn>
              </v-card-actions>
            </template>
          </v-card>
        </v-col>
        <v-col v-if="$vuetify.breakpoint.xlOnly" cols="4">
          <v-card :height="height" class="d-flex flex-column" outlined rounded="lg">
            <v-card-title class="grey lighten-5">
              <span class="text-h6 text-capitalize">Page Preview</span>
            </v-card-title>
            <v-divider></v-divider>
            <v-card-text
              class="grey lighten-5 flex-grow-1 relative d-flex flex-column align-center justify-center overflow-hidden"
            >
              <div ref="wrapper" class="flex-grow-1 relative d-flex justify-center overflow-hidden">
                <svg class="fill-height" viewBox="0 0 337 693" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path
                    d="M42.2366 0C20.7395 0 3.31268 17.4269 3.31268 38.924V87.0152H1.52956C0.684807 87.0152 0 87.7 0 88.5448V110.638C0 111.483 0.684807 112.168 1.52956 112.168H3.31268V133.921H1.52997C0.682608 133.921 0 134.602 0 135.449V179.241C0 180.089 0.682574 180.771 1.52997 180.771H3.31268V192.836H1.52997C0.682608 192.836 0 193.517 0 194.364V238.156C0 239.004 0.682574 239.686 1.52997 239.686H3.31268V653.426C3.31268 674.923 20.7395 692.35 42.2366 692.35H294.828C316.325 692.35 333.752 674.923 333.752 653.426V223.332H335.47C336.317 223.332 337 222.65 337 221.802V150.771C337 149.924 336.317 149.243 335.47 149.243H333.752V38.924C333.752 17.4269 316.325 0 294.828 0H42.2366Z"
                    fill="black"
                  />
                </svg>
                <div class="preview" :style="{ transform: `scale(${previewZoom})` }">
                  <iframe
                    ref="mobilePreviewRef"
                    @load="sendDataToIframeApp"
                    class="frame"
                    v-if="!modalOpen && displayFrame"
                    :src="previewUrl"
                    frameborder="0"
                  ></iframe>
                </div>
              </div>
            </v-card-text>
            <v-divider></v-divider>
            <v-card-actions class="pa-3 grey lighten-5">
              <v-btn icon tile depressed light class="grey lighten-3 text--primary rounded">
                <v-icon>$smartphone</v-icon>
              </v-btn>
              <v-btn icon tile depressed light class="transparent text--grey rounded" @click="previewPage('tablet')">
                <v-icon>$tablet</v-icon>
              </v-btn>
              <v-btn icon tile depressed light class="transparent text--grey rounded" @click="previewPage('desktop')">
                <v-icon>$desktop</v-icon>
              </v-btn>
              <v-spacer></v-spacer>
              <v-btn depressed class="text-capitalize" color="primary" @click="reloadPreview" @keydown="moveToSidebar"
                >Reload</v-btn
              >
            </v-card-actions>
          </v-card>
        </v-col>
      </v-row>
    </v-container>
    <new-page-modal :program="programId" v-if="newPageModal" v-model="newPageModal" />
    <edit-page-modal v-model="editPageModal" />
    <new-program-modal
      v-if="editProgramModal"
      :programId="programId"
      :edit="editProgramModal"
      v-model="editProgramModal"
    />
    <page-preview-modal @modalOpen="modalOpen = $event" :current-page="currentPage === 0 ? 1 : page?.pageNumber" />
    <confirm-publish-dialog
      v-if="publishProgramModal"
      v-model="publishProgramModal"
      :title="autoPublishLiterals.dialog.publish.title"
      :message="autoPublishLiterals.dialog.publish.message"
      :program="program"
      @confirm="onPublishComplete"
    />

    <confirm-dialog
      v-model="confirmationDialog.isVisible"
      @confirm="setDefaultCoverAndSave"
      title="Confirm Landing Page Cover"
      :message="confirmationDialog.message"
      :primaryButtonLabel="confirmationDialog.primaryButtonLabel"
      :secondaryButtonLabel="confirmationDialog.secondaryButtonLabel"
    />
    <confirm-dialog
      v-model="unsavedChangesDialog"
      @confirm="saveAndNavigate"
      @cancel="discardAndNavigate"
      title="Unsaved Changes"
      message="You have unsaved changes on this page. Would you like to save them before leaving?"
      primaryButtonLabel="Save"
      secondaryButtonLabel="Leave without saving"
      tertiaryButtonLabel="Cancel"
    />
  </div>
</template>

<script lang="ts">
  import Vue from 'vue';
  import {
    TopBar,
    NewPageModal,
    EditPageModal,
    InlineFieldBlock,
    LandingPage,
    StaffBlock,
    PagePreviewModal,
    HidePageButton,
    PageReimportButton,
  } from '@/components/Builder';

  import { Card, ConfirmDialog } from '@/components';
  import { mapActions, mapGetters } from 'vuex';
  import { ConfirmPublishDialog, NewProgramModal } from '@/components/Programs';
  import { BLOCKS_MAP } from '@/constants/builder';
  import program from '@/services/program/program';
  import { PROGRAMS_STATUS, AUTO_PUBLISH_LITERALS } from '@/constants/programs';
  import { Device, Page } from '@/store/builder/types';
  import { eventBus } from '../main';
  import draggable from 'vuedraggable';
  import config from '@/constants/config';
  import { LandingPageRef } from '@/components/Builder/LandingPage.vue';
  import ClientService from '@/services/client/client';
  import { VListItem } from 'vuetify/lib/components';
  import { CoverOption, LandingPageData } from '@/components/types';
  import { cloneDeep, isEqual } from 'lodash-es';
  export default Vue.extend({
    name: 'Builder',
    props: {
      programId: {
        type: [String, Number],
        required: true,
      },
    },
    components: {
      TopBar,
      Card,
      NewPageModal,
      InlineFieldBlock,
      NewProgramModal,
      EditPageModal,
      LandingPage,
      StaffBlock,
      PagePreviewModal,
      draggable,
      ConfirmPublishDialog,
      ConfirmDialog,
      HidePageButton,
      PageReimportButton,
    },
    data() {
      return {
        isLoading: false,
        isLoadingImage: false,
        confirmationDialog: {
          isVisible: false,
          message: '',
          primaryButtonLabel: '',
          secondaryButtonLabel: '',
        },
        prevSidebarAction: 'pageItem0',
        lastSidebarAction: '',
        blocksMap: BLOCKS_MAP,
        previewHeight: null as number | null,
        observer: null as ResizeObserver | null,
        newPageModal: false,
        editProgramModal: false,
        publishProgramModal: false,
        currentPage: null as number | null,
        editPageModal: false,
        displayLandingPage: false,
        programLoaded: false,
        displayFrame: true,
        clientName: '',
        sportName: '',
        dialogDelete: false,
        deletePageNumber: null as number | null,
        deletePageId: null as number | null,
        modalOpen: false,
        originalPage: null as Page | null,
        originalLandingPage: null as LandingPageData | null,
        originalPages: [] as Page[],
        pendingPageId: null as number | null,
        unsavedChangesDialog: false,
      };
    },
    computed: {
      ...mapGetters('builder', { pages: 'getPages', page: 'getPage' }),
      ...mapGetters('programs', { program: 'getProgram' }),
      ...mapGetters('user', { token: 'getAuthToken' }),
      height(): number {
        const { $vuetify } = this;
        return $vuetify.breakpoint.height - $vuetify.application.top - 12;
      },
      previewZoom(): number {
        const { previewHeight } = this;
        return (previewHeight - previewHeight * 0.2) / (375 / 0.5625);
      },
      previewUrl(): string {
        const { programId } = this;
        const page = this.currentPage === 0 ? 1 : this.page?.pageNumber;
        return this.clientName !== '' && this.sportName != ''
          ? `${config.frontendUrl}/${this.clientName}/programs/${programId}/${page}?auth-token=${this.token}`
          : '';
      },
      leftColumnSize(): number {
        const { xl, lg } = this.$vuetify.breakpoint;
        if (xl) return 2;
        else if (lg) return 3;
        return 4;
      },
      centerColumnSize(): number {
        const { xl, lg } = this.$vuetify.breakpoint;
        if (xl) return 6;
        else if (lg) return 9;
        return 8;
      },
      canPublish(): boolean {
        return this.program?.status !== PROGRAMS_STATUS.PUBLISHED;
      },
      autoPublishLiterals() {
        return AUTO_PUBLISH_LITERALS;
      },
      pageIsDirty(): boolean {
        if (this.currentPage === 0) {
          const relevantLandingPageData = (landingPage: LandingPageData) =>
            landingPage?.selectedOption === CoverOption.Default
              ? { selectedOption: landingPage.selectedOption }
              : landingPage;
          return !isEqual(
            relevantLandingPageData(this.program?.landingPage),
            relevantLandingPageData(this.originalLandingPage)
          );
        }
        return !isEqual(this.page, this.originalPage);
      },
      orderUpdated(): boolean {
        return this.comparePageOrder(this.originalPages, this.pages) === false;
      },
    },
    methods: {
      fetchSport: ClientService.fetchSport,
      fetchClient: ClientService.fetchClient,
      fetchPublicationPage: ClientService.fetchPublicationPage,
      fetchPublicationSettings: ClientService.fetchPublicationSettings,
      ...mapActions('builder', {
        fetchPages: 'fetchPages',
        fetchPage: 'fetchPage',
        savePage: 'savePage',
        deletePage: 'deletePage',
        setPagePreviewVisibility: 'setPagePreviewVisibility',
        setPagePreviewType: 'setPagePreviewType',
        setPageProgram: 'setPageProgram',
      }),
      ...mapActions('programs', { fetchProgram: 'fetchProgram' }),
      ...mapActions('ui', { showToast: 'showToast' }),
      previewPage(type: Device) {
        this.setPagePreviewType(type);
        this.setPagePreviewVisibility(true);
      },
      toggleNewPageModal() {
        this.editPageModal = false;
        this.newPageModal = !this.newPageModal;
      },
      editProgram() {
        this.editProgramModal = !this.editProgramModal;
      },
      async getProgram(programId: string | number) {
        try {
          await this.fetchProgram(programId);
        } catch (error) {
          // const msg = error?.response?.data || 'Could not fetch program details';
          // console.warn(msg);
        }
      },
      async editPage(pageId: number) {
        await this.selectPage(pageId);
        this.editPageModal = true;
      },
      async confirmRemovePage() {
        try {
          this.dialogDelete = false;
          if (this.currentPage === this.deletePageId) this.currentPage = 0;
          const { programId } = this;
          await this.deletePage({
            programId,
            pageId: this.deletePageId,
          });
        } catch (error) {
          const msg = error?.response?.data || 'Could not delete page';
          this.showToast(msg);
        }
      },
      async removePage(pageNumber: number, pageId: number) {
        this.dialogDelete = true;
        this.deletePageNumber = pageNumber;
        this.deletePageId = pageId;
      },
      async setDefaultCoverAndSave() {
        const form = this.$refs.form as LandingPageRef;
        form.formData.selectedOption = CoverOption.Default;
        this.closeConfirmationDialog();

        this.onPageSave();
      },
      closeConfirmationDialog() {
        this.confirmationDialog = {
          isVisible: false,
          message: '',
          primaryButtonLabel: '',
          secondaryButtonLabel: '',
        };
      },
      showConfirmationDialog(props: any) {
        this.confirmationDialog = {
          isVisible: true,
          ...props,
        };
      },

      async onPageSave() {
        try {
          if (this.currentPage === 0) {
            //update program landing page
            const form = this.$refs.form as LandingPageRef;

            if (form.formData.selectedOption === CoverOption.Custom && !form?.formData?.media?.url) {
              this.showConfirmationDialog({
                message: `No file was uploaded. Would you like to use the <strong>Default Cover</strong>?`,
                primaryButtonLabel: 'Yes',
                secondaryButtonLabel: 'No, I will upload a file',
              });
              return;
            } else if (form.validate()) {
              try {
                this.isLoading = true;
                const res = await program.updateProgramLandingPage({ ...this.program, landingPage: form.formData });
                form.formData = res;
                await this.$nextTick();
              } catch (e) {
                throw new Error(`api error - ${e}`);
              } finally {
                this.isLoading = false;
              }
            } else {
              throw new Error('form error');
            }
          } else {
            const { programId, page } = this;
            await this.savePage({
              programId,
              page,
            });
          }
          this.showToast('Page updated successfully');
        } catch (error) {
          const msg = error?.response?.data || 'Could not save page';
          this.showToast(msg);
        }
      },
      async onSaveProgram() {
        try {
          const pagesIds = this.pages.map(({ id }: { id: number }) => id);

          //update program pages order
          await program.updateProgramPagesOrder({ program: this.program, pagesIds: pagesIds });
          await this.fetchPages(this.program.id);
          if (this.currentPage) {
            //update the original page copy except for the content since that's
            //saved separately via the "Page Builder"
            const originalContent = this.originalPage.content;
            this.refreshOriginalPageCopy();
            this.originalPage.content = originalContent;
          }
          this.refreshOriginalPageListCopy();
          this.showToast('Program updated successfully');
        } catch (error) {
          const msg = error?.response?.data || 'Could not save program';
          this.showToast(msg);
        }
      },
      async selectPage(pageId: number) {
        if (this.pageIsDirty) {
          this.pendingPageId = pageId;
          this.unsavedChangesDialog = true;
          return;
        }
        await this.doSelectPage(pageId);
      },
      async doSelectPage(pageId: number) {
        this.currentPage = pageId;
        this.displayLandingPage = false;
        if (pageId) {
          const { programId } = this;
          await this.fetchPage({ programId, pageId });
          this.refreshOriginalPageCopy();
        }
      },
      async saveAndNavigate() {
        await this.onPageSave();
        await this.doSelectPage(this.pendingPageId);
        this.pendingPageId = null;
      },
      async discardAndNavigate() {
        if (this.currentPage === 0 && this.program) {
          this.refreshOriginalPageCopy();
        }
        await this.doSelectPage(this.pendingPageId);
        this.pendingPageId = null;
      },
      onPublish() {
        this.publishProgramModal = true;
      },
      onPublishComplete() {
        this.publishProgramModal = false;
      },
      reloadPreview() {
        // The following didn't work cause violates cross-origin policies
        // const frame = this.$refs.frame as HTMLIFrameElement;
        // frame.contentWindow.location.reload();
        this.displayFrame = false;
        this.$nextTick(() => {
          this.displayFrame = true;
        });
      },
      updateOrderPage() {
        this.pages.forEach((item: any, index: number) => (item.pageNumber = index + 2));
      },
      selectPageNumberInput(event: FocusEvent) {
        (event.target as HTMLInputElement).select();
      },
      movePageToNumber(page: any, event: Event) {
        const input = event.target as HTMLInputElement;
        const num = parseInt(input.value, 10);
        if (isNaN(num)) {
          input.value = String(page.pageNumber);
          return;
        }
        const clampedNum = Math.max(2, Math.min(this.pages.length + 1, num));
        const currentIndex = this.pages.findIndex((p: any) => p.id === page.id);
        const targetIndex = clampedNum - 2;
        if (currentIndex === targetIndex) {
          input.value = String(page.pageNumber);
          return;
        }
        const [removed] = this.pages.splice(currentIndex, 1);
        this.pages.splice(targetIndex, 0, removed);
        this.updateOrderPage();
      },
      moveNext(evt: KeyboardEvent, element: string) {
        this.lastSidebarAction = element;
        const ref = this.$refs[element] as InstanceType<typeof VListItem>[];
        if (ref[0]) {
          if (ref[0].$el.classList.contains('v-list-item--active') && evt.key == 'Tab' && !evt.shiftKey) {
            this.prevSidebarAction = element;
            evt.preventDefault();
            (this.$refs.pageBuilder as HTMLElement).focus();
          } else if (!ref[0].$el.classList.contains('v-list-item--active') && evt.key == 'Tab' && evt.shiftKey) {
            evt.preventDefault();
            (ref[0].$el as HTMLElement).focus();
          }
        } else {
          const ref2 = this.$refs[element] as InstanceType<typeof VListItem>;
          if (ref2.$el.classList.contains('v-list-item--active') && evt.key == 'Tab' && !evt.shiftKey) {
            this.prevSidebarAction = element;
            evt.preventDefault();
            document.getElementById('fullPageRadio').focus();
          }
        }
      },
      moveToSidebar(evt: KeyboardEvent) {
        const pages = Object.keys(this.$refs).filter((p) => p.includes('pageItem'));
        const idx = pages.indexOf(this.prevSidebarAction);
        const lastIdx = pages.indexOf(this.lastSidebarAction);
        if (evt.key == 'Tab' && !evt.shiftKey && idx < pages.length - 1 && lastIdx != pages.length - 1) {
          evt.preventDefault();
          const ref = this.$refs[pages[idx + 1]] as InstanceType<typeof VListItem>[];
          (ref[0].$el as HTMLElement).focus();
        }
      },
      sendDataToIframeApp() {
        const iframeWindow: Window = (this.$refs.mobilePreviewRef as any).contentWindow;
        if (iframeWindow) {
          iframeWindow.postMessage({ isDashboardPreview: true }, '*');
        }
      },
      comparePageOrder(pages1: Page[], pages2: Page[]) {
        return isEqual(
          [...pages1].sort((p1, p2) => p1.pageNumber - p2.pageNumber).map((p) => p.id),
          [...pages2].sort((p1, p2) => p1.pageNumber - p2.pageNumber).map((p) => p.id)
        );
      },
      refreshOriginalPageCopy() {
        if (this.currentPage) {
          this.originalPage = cloneDeep(this.page);
        } else {
          this.originalLandingPage = cloneDeep(this.program?.landingPage);
        }
      },
      refreshOriginalPageListCopy() {
        this.originalPages = cloneDeep(this.pages);
      },
    },
    async created() {
      try {
        await this.getProgram(this.programId);
        this.setPageProgram(this.programId);
        const { programId } = this;
        const { clientId } = this.program;
        const { clientSportId } = this.program.sport;
        const client = await this.fetchClient(clientId);
        const sport = await this.fetchSport({ clientId, clientSportId });
        this.clientName = client.tenantId;
        this.sportName = sport?.shortName;
        await this.fetchPages(programId);
        this.currentPage = 0;
        this.programLoaded = true;
        this.refreshOriginalPageCopy();
        this.refreshOriginalPageListCopy();
      } catch (error) {
        const msg = error?.response?.data || 'Could not fetch pages';
        this.showToast(msg);
      }
      eventBus.$on('changePage', (pageId: number) => {
        this.selectPage(pageId);
      });
      eventBus.$on('pageSaved', () => {
        this.refreshOriginalPageCopy();
      });
    },
    async mounted() {
      const wrapper = this.$refs.wrapper as HTMLElement;
      if (!wrapper) return;

      const { height } = wrapper.getBoundingClientRect();
      this.previewHeight = height;
      const observer = new ResizeObserver((entries) => {
        const { height } = entries[0].contentRect;
        this.previewHeight = height;
      });
      observer.observe(this.$refs.wrapper as HTMLElement);
      this.observer = observer;
    },
    beforeDestroy() {
      if (this.observer) this.observer.disconnect();
    },
  });
</script>
<style lang="scss" scoped>
  .page-number {
    width: 40px;
    height: 40px;
  }

  .preview {
    position: absolute;
    transform-origin: 50% 0;
    top: 10%;
  }

  .frame {
    aspect-ratio: 9 / 16;
    width: 375px;
  }

  .page-view {
    top: 16px;
    left: 16px;
    z-index: 10;
  }
  .item-list {
    border-bottom: 1px solid rgba(0, 0, 0, 0.12);
  }
  .ghost {
    background: #bdbdbd;
  }
  .page-number-input {
    width: 100%;
    height: 100%;
    background: transparent;
    text-align: center;
    font-size: 0.875rem;
    color: inherit;

    // Hide the spin buttons for number input (▲▼ up/down button)
    &::-webkit-outer-spin-button,
    &::-webkit-inner-spin-button {
      -webkit-appearance: none;
      margin: 0;
    }
    -moz-appearance: textfield;
  }
</style>
